import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import { livetvApi } from '@/services/livetvApi';
import type { LiveTvChannel } from '@/types/livetv';

export type LiveTvStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface LiveTvState {
  channels: LiveTvChannel[];
  byId: Record<string, LiveTvChannel>;
  status: LiveTvStatus;
  error: string | null;
  lastFetchedAt: number | null;
}

const initialState: LiveTvState = {
  channels: [],
  byId: {},
  status: 'idle',
  error: null,
  lastFetchedAt: null,
};

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : 'Failed to load channels';
}

export const fetchLiveTvChannels = createAsyncThunk<
  LiveTvChannel[],
  void,
  { rejectValue: string }
>('livetv/fetchChannels', async (_arg, { rejectWithValue }) => {
  try {
    const { channels } = await livetvApi.listChannels();
    return channels;
  } catch (err) {
    return rejectWithValue(describeError(err));
  }
});

const livetvSlice = createSlice({
  name: 'livetv',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveTvChannels.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchLiveTvChannels.fulfilled, (state, action) => {
        state.status = 'ready';
        state.channels = action.payload;
        const next: Record<string, LiveTvChannel> = {};
        for (const c of action.payload) next[c.id] = c;
        state.byId = next;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchLiveTvChannels.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to load channels';
      });
  },
});

export function selectLiveTvChannelById(
  state: RootState,
  channelId: string,
): LiveTvChannel | undefined {
  return state.livetv.byId[channelId];
}

export default livetvSlice.reducer;
